import { format } from "date-fns";
import { Decimal } from "decimal.js";
import moment from "moment";

export const formatToPercentage = (value?: Decimal | string | null) => {
  if (value === undefined || value === null) return "--";
  if (typeof value === "string") value = new Decimal(value);

  return `${value.times(100).toDecimalPlaces(2)}%`;
};

export const formatToCurrency = (
  value?: Decimal | string | number | null,
  currency?: boolean,
  currentCurrency?: string,
  language?: string,
) => {
  if (value === undefined || value === null) return "--";

  // Remove any non-numeric characters except for the decimal point
  if (typeof value === "string") {
    value = value.replace(/[^\d.]/g, "");
  }

  if (typeof value === "string" || typeof value === "number") {
    try {
      value = new Decimal(value);
    } catch (e) {
      return "--";
    }
  }

  try {
    const amount = value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    if (currency === false) return amount;

    if (!currentCurrency) {
      return language === "ar" ? `${amount} د.إ.` : `AED ${amount}`;
    }
    return language === "ar"
      ? `${amount} ${currentCurrency}`
      : `${currentCurrency} ${amount}`;
  } catch (e) {
    return "--";
  }
};

export const formatNumber = (value?: Decimal | string | number | null) => {
  if (value === undefined || value === null) return "--";
  if (typeof value === "string" || typeof value === "number")
    value = new Decimal(value);

  try {
    return value
      .toFixed(value.mod(1).equals(0) ? 0 : 2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  } catch (e) {
    return "--";
  }
};

export const formatPercentage = (value?: Decimal | string | null) => {
  if (value === undefined || value === null) return "--";
  if (typeof value === "string") value = new Decimal(value);

  return `${value.times(100)}%`;
};

export const formatFileSize = (fileSize: number): string => {
  if (fileSize < 1024) return `${fileSize} B`;
  if (fileSize < 1024 * 1024) return `${(fileSize / 1024).toFixed(2)} KB`;
  return `${(fileSize / (1024 * 1024)).toFixed(2)} MB`;
};

export const formatHtmlDateInput = (value: Date | string): string => {
  return format(new Date(value), "yyyy-MM-dd");
};

export const convertDateToDateTime = (date: Date) => {
  if (!date) return null;
  const currentDate = moment(date).format("YYYY-MM-DD");

  return moment(`${currentDate} ${moment().format("HH:mm:ss")}`).toDate();
};

export const formatISODate = (
  date: Date | string | undefined,
): string | null => {
  if (!date) return null;
  return moment(date).format("YYYY-MM-DD");
};

export const convertLocalDateToUTC = (date: Date) => {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000);
};

export const downloadFile = async (url: string, filename: string) => {
  await fetch(url)
    .then((response) => response.blob())
    .then((blob) => {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
    });
};

export const downloadFileByClick = async (href: string) => {
  const link = document.createElement("a");
  link.href = href;
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
};

export const MB = 1024 * 1024;

export const formatDateToString = (date: Date | undefined) => {
  if (!date) return "";
  return moment(date).format("YYYY-MM-DD");
};

export const parseStringToDate = (dateString: string) => {
  if (!dateString) return undefined;
  return moment(dateString, "YYYY-MM-DD").toDate();
};

export const toSentenceCase = (str: string): string => {
  if (!str) return str;
  const lower = str.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

export const generateBadges = (minRate: number, maxRate: number) => {
  const min = new Decimal(minRate).mul(100).round().toNumber();
  const max = new Decimal(maxRate).mul(100).round().toNumber();

  if (min >= max) return [max];

  const steps = [5, 10, 15, 20, 25];

  let chosenStep = 5;

  for (const step of steps) {
    const count = new Decimal(max).sub(min).div(step).floor().toNumber() + 1;
    if (count >= 3 && count <= 5) {
      chosenStep = step;
      break;
    }
  }

  const badges: number[] = [];

  const start = new Decimal(min).div(chosenStep).ceil().toNumber() * chosenStep;
  for (let v = start; v < max; v += chosenStep) {
    badges.push(new Decimal(v).toNumber());
  }

  if (badges.length > 5) {
    const trimmed = badges.slice(0, 4);
    trimmed.push(new Decimal(max).toNumber());
    return trimmed;
  }

  while (badges.length < 3) {
    const first = new Decimal(badges[0]).toNumber();
    const prev = new Decimal(first).sub(chosenStep).toNumber();
    if (prev >= min) {
      badges.unshift(new Decimal(prev).toNumber());
    } else break;
  }

  return badges;
};

export const formatColumnLabel = (value: string) => {
  return value
    .replace(/[_\s]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
